import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { ChevronLeft, ChevronRight, Building2, Users, Plus, Edit, Trash2, FolderOpen, BarChart3 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { fmtDate } from '../lib/dateUtils';

const MONTH_NAMES = ['','Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const DAY_NAMES = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];

const STATUS_COLORS = {
  attivo: 'bg-green-200 border-green-400 text-green-800',
  pianificato: 'bg-blue-200 border-blue-400 text-blue-800',
  sospeso: 'bg-amber-200 border-amber-400 text-amber-800',
  chiuso: 'bg-slate-200 border-slate-400 text-slate-600',
  completato: 'bg-emerald-200 border-emerald-400 text-emerald-800'
};

const PROJECT_COLORS = [
  'bg-blue-100 border-blue-300 text-blue-800',
  'bg-emerald-100 border-emerald-300 text-emerald-800',
  'bg-purple-100 border-purple-300 text-purple-800',
  'bg-orange-100 border-orange-300 text-orange-800',
  'bg-pink-100 border-pink-300 text-pink-800',
  'bg-cyan-100 border-cyan-300 text-cyan-800',
  'bg-red-100 border-red-300 text-red-800',
  'bg-yellow-100 border-yellow-300 text-yellow-800',
];

const emptyForm = {
  site_id: '', name: '', description: '', start_date: '', end_date: '',
  progress_percentage: 0, responsible: '', budget: '', status: 'attivo', notes: '',
  work_order: '', purchase_order: '', referente_tecnico: '', numero_pdl: ''
};

export default function ProjectCalendarPage() {
  const navigate = useNavigate();
  const { isRole } = useAuth();
  const canEdit = isRole('admin', 'aspp', 'qhse', 'capo_cantiere');
  const [sites, setSites] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [expandedSites, setExpandedSites] = useState({});

  const load = async () => {
    try {
      const [projectsRes, allSitesRes] = await Promise.all([
        api.get('/projects'),
        api.get('/sites')
      ]);
      const allSitesList = allSitesRes.data || [];
      const projectsList = projectsRes.data || [];
      setAllSites(allSitesList);
      // Group projects by site
      const siteMap = {};
      allSitesList.forEach(s => { siteMap[s.id] = { ...s, projects: [] }; });
      projectsList.forEach(p => {
        if (p.site_id && siteMap[p.site_id]) {
          siteMap[p.site_id].projects.push(p);
        } else {
          if (!siteMap['__unsorted__']) siteMap['__unsorted__'] = { id: '__unsorted__', name: 'Senza Cantiere', projects: [] };
          siteMap['__unsorted__'].projects.push(p);
        }
      });
      const grouped = Object.values(siteMap).filter(s => s.projects.length > 0);
      setSites(grouped);
      const expanded = {};
      grouped.forEach(s => { expanded[s.id] = true; });
      setExpandedSites(expanded);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const changeMonth = (d) => {
    let m = month + d, y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setMonth(m); setYear(y);
  };

  const getDaysInMonth = (m, y) => new Date(y, m, 0).getDate();
  const getFirstDay = (m, y) => { const d = new Date(y, m-1, 1).getDay(); return d === 0 ? 6 : d - 1; };
  const totalDays = getDaysInMonth(month, year);
  const firstDay = getFirstDay(month, year);
  const today = new Date();

  // Check if a project is active on a given date
  const isProjectActiveOnDate = (proj, dateStr) => {
    if (proj.status === 'completato') return false;
    if (!proj.start_date && !proj.end_date) return proj.status === 'attivo';
    if (proj.start_date && proj.start_date > dateStr) return false;
    if (proj.end_date && proj.end_date < dateStr) return false;
    return proj.status !== 'completato';
  };

  // Flatten all projects with site info and color
  const getAllProjects = () => {
    const all = [];
    sites.forEach((s, si) => {
      s.projects.forEach((p, pi) => {
        all.push({ ...p, site_name: s.name, site_id: s.id, colorClass: PROJECT_COLORS[(si * 3 + pi) % PROJECT_COLORS.length] });
      });
    });
    return all;
  };

  const allProjects = getAllProjects();
  const totalProjects = allProjects.length;
  const activeProjects = allProjects.filter(p => p.status === 'attivo').length;

  const openCreate = (siteId) => {
    setEditProject(null);
    setForm({ ...emptyForm, site_id: siteId || '' });
    setShowForm(true);
  };

  const openEdit = (project) => {
    setEditProject(project);
    setForm({
      site_id: project.site_id || '',
      name: project.name || '',
      description: project.description || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      progress_percentage: project.progress_percentage || 0,
      responsible: project.responsible || '',
      budget: project.budget?.toString() || '',
      status: project.status || 'attivo',
      notes: project.notes || '',
      work_order: project.work_order || '',
      purchase_order: project.purchase_order || '',
      referente_tecnico: project.referente_tecnico || '',
      numero_pdl: project.numero_pdl || ''
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        progress_percentage: parseInt(form.progress_percentage) || 0,
        budget: form.budget ? parseFloat(form.budget) : null
      };
      if (editProject) {
        await api.put('/projects/' + editProject.id, payload);
      } else {
        await api.post('/projects', payload);
      }
      setShowForm(false);
      setEditProject(null);
      setForm({ ...emptyForm });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Eliminare questo progetto?')) return;
    try { await api.delete('/projects/' + projectId); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  // Week view
  const getWeekDates = () => {
    const curr = new Date(year, month - 1, today.getDate());
    const dayOfWeek = curr.getDay() === 0 ? 6 : curr.getDay() - 1;
    const monday = new Date(curr);
    monday.setDate(curr.getDate() - dayOfWeek);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const toggleSite = (siteId) => {
    setExpandedSites(prev => ({ ...prev, [siteId]: !prev[siteId] }));
  };

  if (loading) return <div className="animate-pulse text-center py-20 text-slate-400">Caricamento...</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Calendario Progetti</h1>
          <p className="text-sm text-slate-500 mt-0.5">{sites.length} cantieri - {totalProjects} progetti ({activeProjects} attivi)</p>
        </div>
        {canEdit && (
          <Button onClick={() => openCreate('')} data-testid="add-project-btn">
            <Plus size={16} className="mr-1" /> Nuovo Progetto
          </Button>
        )}
      </div>

      {/* Cantieri con Progetti */}
      <div className="space-y-3">
        {sites.map((site, si) => (
          <Card key={site.id} className="border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50" onClick={() => toggleSite(site.id)}>
              <Building2 size={18} className="text-slate-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm truncate">{site.name}</span>
                  <Badge variant="outline" className={STATUS_COLORS[site.status] || ''}>{site.status}</Badge>
                  <span className="text-xs text-slate-400">{site.client}</span>
                </div>
              </div>
              <Badge variant="outline" className="bg-slate-50">
                <FolderOpen size={12} className="mr-1" />{site.projects.length} progetti
              </Badge>
              {canEdit && (
                <Button variant="ghost" size="sm" className="text-blue-600 h-7 px-2" onClick={(e) => { e.stopPropagation(); openCreate(site.id); }}>
                  <Plus size={14} />
                </Button>
              )}
            </div>
            {expandedSites[site.id] && site.projects.length > 0 && (
              <div className="px-3 pb-3 space-y-2">
                {site.projects.map((p, pi) => {
                  const pColor = p.status === 'completato' ? 'bg-emerald-500' : p.progress_percentage >= 60 ? 'bg-blue-500' : p.progress_percentage >= 30 ? 'bg-amber-500' : 'bg-slate-400';
                  return (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => navigate('/projects/' + p.id)} data-testid={"project-" + p.id}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{p.name}</span>
                          <Badge variant="outline" className={STATUS_COLORS[p.status] || 'bg-slate-100'}>{p.status}</Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          {p.responsible && <span>Resp: {p.responsible}</span>}
                          {p.start_date && <span>{fmtDate(p.start_date)}{p.end_date ? ' → ' + fmtDate(p.end_date) : ''}</span>}
                          {p.budget != null && <span>Budget: {p.budget.toLocaleString('it-IT')} EUR</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-24 bg-slate-200 rounded-full h-2.5">
                          <div className={"h-2.5 rounded-full transition-all " + pColor} style={{ width: Math.min(100, p.progress_percentage) + '%' }} />
                        </div>
                        <span className="text-xs font-bold w-8 text-right">{p.progress_percentage}%</span>
                        {canEdit && (
                          <div className="flex gap-0.5">
                            <Button variant="ghost" size="sm" className="p-1 h-auto text-blue-600" onClick={(e) => { e.stopPropagation(); openEdit({ ...p, site_id: site.id }); }} data-testid={"edit-proj-" + p.id}><Edit size={13} /></Button>
                            <Button variant="ghost" size="sm" className="p-1 h-auto text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} data-testid={"del-proj-" + p.id}><Trash2 size={13} /></Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {expandedSites[site.id] && site.projects.length === 0 && (
              <div className="px-3 pb-3 text-xs text-slate-400 text-center py-4">
                Nessun progetto. {canEdit && <span className="text-blue-500 cursor-pointer" onClick={() => openCreate(site.id)}>Aggiungine uno</span>}
              </div>
            )}
          </Card>
        ))}
        {sites.length === 0 && <p className="text-slate-400 text-center py-8">Nessun cantiere attivo o pianificato</p>}
      </div>

      <Tabs defaultValue="mensile">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="mensile">Vista Mensile</TabsTrigger>
          <TabsTrigger value="settimanale">Vista Settimanale</TabsTrigger>
          <TabsTrigger value="gantt">Timeline</TabsTrigger>
        </TabsList>

        {/* Monthly View */}
        <TabsContent value="mensile">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={() => changeMonth(-1)}><ChevronLeft size={18} /></Button>
                <CardTitle className="text-base">{MONTH_NAMES[month]} {year}</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => changeMonth(1)}><ChevronRight size={18} /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-slate-500 mb-2">
                {DAY_NAMES.map(d => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => <div key={"e-" + i} />)}
                {Array.from({ length: totalDays }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = year + '-' + String(month).padStart(2,'0') + '-' + String(day).padStart(2,'0');
                  const dow = new Date(year, month-1, day).getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  const activeProjs = allProjects.filter(p => isProjectActiveOnDate(p, dateStr));
                  const isToday = today.getDate() === day && today.getMonth()+1 === month && today.getFullYear() === year;
                  return (
                    <div key={day} className={"min-h-[70px] border rounded p-1 text-xs " + (isWeekend ? 'bg-slate-50 ' : '') + (isToday ? 'border-blue-500 border-2' : 'border-slate-200')}>
                      <div className={"font-medium mb-0.5 " + (isWeekend ? 'text-red-500 ' : '') + (isToday ? 'text-blue-600 font-bold' : '')}>{day}</div>
                      {activeProjs.slice(0, 3).map(p => (
                        <div key={p.id} className={"text-[9px] leading-tight truncate rounded px-0.5 mb-0.5 border cursor-pointer " + p.colorClass}
                          onClick={() => openEdit(p)} title={p.site_name + ' - ' + p.name + ' (' + p.progress_percentage + '%)'}>
                          {p.name}
                        </div>
                      ))}
                      {activeProjs.length > 3 && <div className="text-[9px] text-slate-400">+{activeProjs.length - 3}</div>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly View */}
        <TabsContent value="settimanale">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-7 gap-2">
                {getWeekDates().map((d, i) => {
                  const dateStr = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
                  const dow = d.getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  const isToday = d.toDateString() === today.toDateString();
                  const activeProjs = allProjects.filter(p => isProjectActiveOnDate(p, dateStr));
                  return (
                    <div key={i} className={"min-h-[120px] border rounded-lg p-2 " + (isToday ? 'border-blue-500 border-2 bg-blue-50 ' : '') + (isWeekend ? 'bg-slate-50' : '')}>
                      <div className={"text-xs font-medium mb-2 " + (isWeekend ? 'text-red-500 ' : '') + (isToday ? 'text-blue-600 font-bold' : 'text-slate-500')}>
                        {DAY_NAMES[i]} {d.getDate()}/{d.getMonth()+1}
                      </div>
                      {activeProjs.map(p => (
                        <div key={p.id} className={"text-xs p-1.5 rounded mb-1 border cursor-pointer hover:shadow " + p.colorClass} onClick={() => openEdit(p)}>
                          <p className="font-medium truncate">{p.name}</p>
                          <p className="text-[10px] opacity-70 truncate">{p.site_name}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <div className="flex-1 bg-white/50 rounded-full h-1.5">
                              <div className="h-1.5 rounded-full bg-current opacity-60" style={{ width: Math.min(100, p.progress_percentage) + '%' }} />
                            </div>
                            <span className="text-[10px] font-bold">{p.progress_percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline View */}
        <TabsContent value="gantt">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Timeline Progetti per Cantiere</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sites.map(s => (
                  <div key={s.id}>
                    <div className="flex items-center gap-2 mb-2 cursor-pointer" onClick={() => navigate('/site-planning/' + s.id)}>
                      <Building2 size={16} className="text-slate-500" />
                      <span className="text-sm font-bold text-slate-700">{s.name}</span>
                      <Badge variant="outline" className={STATUS_COLORS[s.status] || ''}>{s.status}</Badge>
                      <span className="text-xs text-slate-400 ml-1">{s.client}</span>
                    </div>
                    {s.projects.length === 0 ? (
                      <p className="text-xs text-slate-400 ml-6 mb-2">Nessun progetto</p>
                    ) : s.projects.map(p => {
                      const pColor = p.status === 'completato' ? 'bg-emerald-500' : p.progress_percentage >= 60 ? 'bg-blue-500' : p.progress_percentage >= 30 ? 'bg-amber-500' : 'bg-slate-400';
                      return (
                        <div key={p.id} className="flex items-center gap-3 ml-6 mb-2 cursor-pointer hover:bg-slate-50 rounded p-1.5" onClick={() => openEdit({ ...p, site_id: s.id })}>
                          <div className="w-44 flex-shrink-0">
                            <p className="text-sm font-medium truncate">{p.name}</p>
                            <p className="text-xs text-slate-500">{p.responsible || '-'}</p>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className={STATUS_COLORS[p.status] || 'bg-slate-100'}>{p.status}</Badge>
                              <span className="text-xs text-slate-500">{p.start_date ? fmtDate(p.start_date) : '?'} - {p.end_date ? fmtDate(p.end_date) : '?'}</span>
                              {p.budget != null && <span className="text-xs text-slate-500 ml-auto">{p.budget.toLocaleString('it-IT')} EUR</span>}
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-3.5 relative">
                              <div className={"h-3.5 rounded-full transition-all " + pColor} style={{ width: Math.min(100, p.progress_percentage) + '%' }}>
                                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-700">{p.progress_percentage}%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {sites.length === 0 && <p className="text-slate-500 text-center py-6">Nessun cantiere attivo/pianificato</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Project Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editProject ? 'Modifica Progetto' : 'Nuovo Progetto'}</DialogTitle>
            <DialogDescription>{editProject ? 'Aggiorna i dati del progetto' : 'Aggiungi un nuovo progetto a un cantiere'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Cantiere *</Label>
              <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })} disabled={!!editProject}>
                <SelectTrigger data-testid="proj-site-select"><SelectValue placeholder="Seleziona cantiere..." /></SelectTrigger>
                <SelectContent>
                  {allSites.map(s => <SelectItem key={s.id} value={s.id}>{s.name} - {s.client}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nome Progetto *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es: Manutenzione tubazioni" data-testid="proj-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descrizione del progetto..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Data Inizio</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="proj-start" />
              </div>
              <div className="space-y-1.5">
                <Label>Data Fine Prevista</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="proj-end" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Responsabile</Label>
                <Input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} placeholder="Nome responsabile" />
              </div>
              <div className="space-y-1.5">
                <Label>Referente Tecnico</Label>
                <Input value={form.referente_tecnico} onChange={(e) => setForm({ ...form, referente_tecnico: e.target.value })} placeholder="Es: Ing. Rossi" data-testid="proj-referente" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Work Order (WO)</Label>
                <Input value={form.work_order} onChange={(e) => setForm({ ...form, work_order: e.target.value })} placeholder="Es: WO-2026-001" data-testid="proj-wo" />
              </div>
              <div className="space-y-1.5">
                <Label>Purchase Order (PO)</Label>
                <Input value={form.purchase_order} onChange={(e) => setForm({ ...form, purchase_order: e.target.value })} placeholder="Es: PO-2026-001" data-testid="proj-po" />
              </div>
              <div className="space-y-1.5">
                <Label>Numeri PDL</Label>
                <Input value={form.numero_pdl} onChange={(e) => setForm({ ...form, numero_pdl: e.target.value })} placeholder="Es: PDL-001, PDL-002, PDL-003" data-testid="proj-pdl" />
                <p className="text-xs text-slate-400">Separa piu' PDL con la virgola</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Budget (EUR)</Label>
              <Input type="number" step="0.01" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="Opzionale" />
            </div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="proj-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="pianificato">Pianificato</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="completato">Completato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Avanzamento</Label>
                <span className="text-sm font-bold text-slate-700">{form.progress_percentage}%</span>
              </div>
              <Slider
                value={[parseInt(form.progress_percentage) || 0]}
                onValueChange={(v) => setForm({ ...form, progress_percentage: v[0] })}
                min={0} max={100} step={5}
                className="w-full"
                data-testid="proj-progress"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note aggiuntive..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditProject(null); }}>Annulla</Button>
            <Button onClick={handleSave} disabled={!form.name || !form.site_id} data-testid="proj-save-btn">
              {editProject ? 'Salva Modifiche' : 'Crea Progetto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
